import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useOutletContext } from "react-router-dom";
import axios from "../../../../config/axios";
import { FeedbackContext } from "../../../../contexts/FeedbackContext";
import type { ProductionFashionOutletType } from "..";
import FashionApiService from "../../../../services/FashionApiService";
import ApiService from "../../../../services/ApiService";
import eventBus from "../../../../utils/eventBus";
import plus from "../../../../assets/certificates/AddParameter/blue-plus.svg";
import save from "../../../../assets/certificates/AddParameter/save.svg";
import trash from "../../../../assets/production-flow/trash.svg";
import {
  MainContainer,
  SectionName,
  BreadcrumbsContainer,
  StyledCard,
  CardTitle,
  ActionButtons,
  CancelButton,
  SaveButton,
  StyledFormGroup,
  AddButton,
  DropdownList,
  DropdownItem,
} from "../../ProductionFlow/CreateOP/style";

const pageMainStyle: React.CSSProperties = {
  minHeight: "unset",
  height: "auto",
  paddingBottom: 0,
  boxSizing: "border-box",
};

type PlanLine = { key: string; stockOrResourceId: number | null; qty: string };

type FashionOpProductionItem = {
  id: number;
  fashionOrderId: number;
  quantityForThisProduction: number;
};

type LinkedStocksPayload = {
  linkedStocksByScriptMaterial?: Array<{
    scriptMaterialId: number;
    materialId: number;
    stocks: { id: number; description: string | null }[];
    available?: number;
    needed?: number;
    unitName?: string;
  }>;
  scriptMaterialAvailability?: Array<{
    scriptMaterialId: number;
    available: number;
    needed: number;
    unitName: string;
  }>;
  scriptResourceAvailability?: Array<{
    scriptResourceId: number;
    available: number;
    needed: number;
    unitName: string;
  }>;
};

function newKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function matCompositeKey(opItemId: number, scriptMaterialId: number) {
  return `${opItemId}:${scriptMaterialId}`;
}

function insCompositeKey(opItemId: number, scriptResourceId: number) {
  return `${opItemId}:${scriptResourceId}`;
}

function optionsExcludingUsedOnOtherLines<T extends { id: number }>(
  options: T[],
  lines: PlanLine[],
  currentLineKey: string,
  currentSelection: number | null,
): T[] {
  const usedElsewhere = new Set(
    lines
      .filter((l) => l.key !== currentLineKey)
      .map((l) => l.stockOrResourceId)
      .filter((id): id is number => id != null),
  );
  return options.filter(
    (o) =>
      (currentSelection != null && o.id === currentSelection) ||
      !usedElsewhere.has(o.id),
  );
}

function parseQtyInput(raw: string): number | null {
  const t = String(raw ?? "").replace(",", ".").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const FASHION_INS_MOVEMENT_RE = /^__INS_RES_(\d+)__$/;

function fashionMovementOrderPrefix(oid: number): string {
  return `OP-FASHION-${oid}|`;
}

function normalizeMovementsPayload(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as Record<string, unknown>[];
    if (Array.isArray(o.items)) return o.items as Record<string, unknown>[];
  }
  return [];
}

function isFashionStockMovementActive(status: unknown): boolean {
  const t = String(status ?? "").trim();
  return t === "200" || t.startsWith("200");
}

function numOrNull(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

async function fetchStockRowViaProductionFlowRawMaterials(
  stockId: number,
): Promise<Record<string, unknown> | null> {
  try {
    const res = await axios.get(`/production-flow/service/utils/raw-materials/${stockId}`);
    const d = res.data as Record<string, unknown>;
    if (!d || typeof d !== "object") return null;
    return d;
  } catch {
    return null;
  }
}

async function resolveStockIdForCodeAndMaterial(
  code: string,
  materialId: number,
): Promise<number | null> {
  if (!code) return null;
  try {
    const res = await ApiService.stock.findAll({ global: code, size: 80 });
    const payload = (res as { data?: unknown }).data;
    const list = Array.isArray(payload)
      ? payload
      : (payload as { items?: unknown[] })?.items ?? [];
    if (!Array.isArray(list)) return null;
    const byMat = list.filter((s: Record<string, unknown>) => {
      const c = String((s.Code as { code?: string } | undefined)?.code ?? s.code ?? "");
      return c === code && Number(s.materialId) === materialId;
    });
    const row = byMat[0] ?? list.find((s: Record<string, unknown>) => {
      const c = String((s.Code as { code?: string } | undefined)?.code ?? s.code ?? "");
      return c === code;
    });
    return row && (row as { id?: number }).id != null ? Number((row as { id: number }).id) : null;
  } catch {
    return null;
  }
}

const FASHION_MOVEMENT_PAGE_SIZE = 120;

async function fetchActiveFashionMovementsForOrder(
  orderId: number,
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let cursor: number | undefined;
  for (let guard = 0; guard < 100; guard++) {
    const params: Record<string, string | number | boolean> = {
      fashionOnly: true,
      fashionProductionOrderId: orderId,
      size: FASHION_MOVEMENT_PAGE_SIZE,
      global: "",
    };
    if (cursor != null) params.cursor = cursor;
    const res = await ApiService.movements.find(params);
    const list = normalizeMovementsPayload((res as { data?: unknown }).data);
    if (list.length === 0) break;
    out.push(...list);
    if (list.length < FASHION_MOVEMENT_PAGE_SIZE) break;
    const last = list[list.length - 1] as { id?: number };
    const lid = Number(last?.id);
    if (!Number.isFinite(lid) || lid === cursor) break;
    cursor = lid;
  }
  return out;
}

function parseOpProductionItemsFromOrder(ord: Record<string, unknown>): FashionOpProductionItem[] {
  const raw = ord.fashionProductionItems as unknown[] | undefined;
  if (!raw?.length) return [];
  return raw
    .map((it) => {
      const row = it as Record<string, unknown>;
      const id = row.id as number | undefined;
      const fashionOrderId = row.fashionOrderId as number | undefined;
      const quantityForThisProduction = row.quantityForThisProduction as number | undefined;
      if (id == null || fashionOrderId == null) return null;
      return {
        id,
        fashionOrderId,
        quantityForThisProduction: quantityForThisProduction ?? 0,
      };
    })
    .filter((x): x is FashionOpProductionItem => x != null);
}

function extractScriptArraysFromProduct(product: Record<string, unknown> | null): {
  scriptMaterials: Array<{
    id: number;
    materialId: number;
    weight?: number | null;
    unitOfMeasurementId?: number | null;
    UnitOfMeasurement?: { name?: string };
  }>;
  scriptResources: Array<{
    id: number;
    resourceId?: number;
    materialId?: number | null;
    weight?: number | null;
    unitOfMeasurementId?: number | null;
    UnitOfMeasurement?: { name?: string };
    Resource?: { id?: number; name?: string; stockUnit?: string; lengthInMm?: number; unitQuantity?: number; totalWeight?: number; materialId?: number | null; itemColorId?: number | null };
  }>;
} {
  const script = (product?.Script as unknown[])?.[0] as
    | {
        ScriptMaterial?: Array<{
          id: number;
          materialId: number;
          weight?: number | null;
          unitOfMeasurementId?: number | null;
          UnitOfMeasurement?: { name?: string };
        }>;
        ScriptResource?: Array<{
          id: number;
          resourceId?: number;
          materialId?: number | null;
          weight?: number | null;
          unitOfMeasurementId?: number | null;
          UnitOfMeasurement?: { name?: string };
          Resource?: { id?: number; name?: string; stockUnit?: string; lengthInMm?: number; unitQuantity?: number; totalWeight?: number; materialId?: number | null; itemColorId?: number | null };
        }>;
      }
    | undefined;
  return {
    scriptMaterials: script?.ScriptMaterial ?? [],
    scriptResources: script?.ScriptResource ?? [],
  };
}

function mergePlanWithDefaults(
  matPartial: Record<string, PlanLine[]>,
  insPartial: Record<string, PlanLine[]>,
  opItems: FashionOpProductionItem[],
  sms: Array<{ id: number; materialId: number }>,
  srs: Array<{ id: number }>,
): { mat: Record<string, PlanLine[]>; ins: Record<string, PlanLine[]> } {
  const mat: Record<string, PlanLine[]> = {};
  const ins: Record<string, PlanLine[]> = {};
  for (const fpi of opItems) {
    for (const sm of sms) {
      const k = matCompositeKey(fpi.id, sm.id);
      const lines = matPartial[k];
      mat[k] =
        lines && lines.length > 0
          ? lines
          : [{ key: newKey(`m${fpi.id}-${sm.id}`), stockOrResourceId: null, qty: "" }];
    }
    for (const sr of srs) {
      const k = insCompositeKey(fpi.id, sr.id);
      const lines = insPartial[k];
      ins[k] =
        lines && lines.length > 0
          ? lines
          : [{ key: newKey(`r${fpi.id}-${sr.id}`), stockOrResourceId: null, qty: "" }];
    }
  }
  return { mat, ins };
}

function collectResourcesForScriptResource(
  pics: unknown[],
  scriptResourceMaterialId: number | null | undefined,
): { id: number; label: string }[] {
  if (scriptResourceMaterialId == null) return [];
  const seen = new Set<number>();
  const out: { id: number; label: string }[] = [];
  for (const pic of pics) {
    const res = (pic as { Resource?: { id?: number; materialId?: number | null; name?: string } })
      .Resource;
    if (!res?.id || res.materialId !== scriptResourceMaterialId) continue;
    if (seen.has(res.id)) continue;
    seen.add(res.id);
    out.push({ id: res.id, label: res.name || `Insumo #${res.id}` });
  }
  return out;
}

function materialItemColorNameFromPics(pics: unknown[], materialId: number): string {
  for (const p of pics) {
    const row = p as {
      StockColors?: {
        Stock?: { materialId?: number | null };
        ItemColor?: { name?: string };
      } | null;
    };
    if (row.StockColors?.Stock?.materialId !== materialId) continue;
    const n = row.StockColors?.ItemColor?.name?.trim();
    if (n) return n;
  }
  return "—";
}

function insumoItemColorNameFromPic(pic: unknown | undefined): string {
  if (!pic) return "—";
  const n = (
    pic as { Resource?: { ItemColor?: { name?: string } } | null }
  ).Resource?.ItemColor?.name?.trim();
  return n || "—";
}

function qtyInputToInternal(typed: number, unitToken: string): number {
  const u = unitToken.trim().toUpperCase();
  if (u === "UN" || u === "UNIDADE" || u === "UNIDADES" || u === "UND") return Math.round(typed);
  if (u === "M") return typed * 1000;
  if (u === "G" || u === "GRAMAS") return typed / 1000;
  return typed;
}

function stockAvailableForMaterialRow(stock: Record<string, unknown>, unitName: string): number {
  const u = unitName.trim().toUpperCase();
  if (u === "UN" || u === "UNIDADE" || u === "UNIDADES" || u === "UND") return Number(stock.quantity ?? 0);
  if (u === "KG" || u === "QUILOGRAMAS" || u === "GRAMAS" || u === "G") return Number(stock.totalWeight ?? 0);
  const v = stock.lengthInMm ?? stock.quantity;
  return v == null ? 0 : Number(v);
}

function resourceAvailableAmount(res: Record<string, unknown>): number {
  const u = String(res.stockUnit ?? "")
    .trim()
    .toUpperCase();
  if (u === "UN" || u === "UNIDADE" || u === "UNIDADES" || u === "UND") return Number(res.unitQuantity ?? 0);
  if (u === "KG" || u === "QUILOGRAMAS" || u === "GRAMAS" || u === "G") return Number(res.totalWeight ?? 0);
  const len = res.lengthInMm ?? res.unitQuantity;
  return len == null ? 0 : Number(len);
}

function formatQtyLabel(n: number): string {
  return n.toFixed(2);
}

function formatInternalQtyForDisplay(internalValue: number, unitToken: string): string {
  const u = unitToken.trim().toUpperCase();
  if (u === "M") return formatQtyLabel(internalValue / 1000);
  return formatQtyLabel(internalValue);
}

function unitLabelForDisplay(unitToken: string): string {
  const t = unitToken.trim();
  if (!t) return "—";
  if (t.toUpperCase() === "M") return "M";
  return t;
}

const StartFashionProduction: React.FC = () => {
  const { orderId: orderIdParam } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { setRefresh } = useOutletContext<ProductionFashionOutletType>();
  const { setFeedback } = useContext(FeedbackContext);
  const orderId = orderIdParam ? parseInt(orderIdParam, 10) : NaN;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<Record<string, unknown> | null>(null);
  const [product, setProduct] = useState<Record<string, unknown> | null>(null);
  const [materialAvailability, setMaterialAvailability] = useState<LinkedStocksPayload | null>(null);

  const [materialPlanLines, setMaterialPlanLines] = useState<Record<string, PlanLine[]>>({});
  const [insumoPlanLines, setInsumoPlanLines] = useState<Record<string, PlanLine[]>>({});
  const [stockCache, setStockCache] = useState<Record<number, Record<string, unknown>>>({});
  const [resourceCache, setResourceCache] = useState<Record<number, Record<string, unknown>>>({});
  const [openPicker, setOpenPicker] = useState<string | null>(null);
  const [pickerQuery, setPickerQuery] = useState("");

  const orderStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    orderStatusRef.current = order?.status as string | undefined;
  }, [order?.status]);

  const load = useCallback(async () => {
    if (!Number.isFinite(orderId)) {
      setError("OP inválida");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const ordRes = await FashionApiService.getProductionOrderById(orderId);
      const ord = ordRes.data as Record<string, unknown>;
      const fpId =
        (ord.fashionProductId as number | undefined) ??
        ((ord.fashionProduct as { id?: number } | undefined)?.id);

      const [prodRes, avRes] = await Promise.all([
        fpId ? FashionApiService.getFashionProductById(fpId) : Promise.resolve({ data: null }),
        FashionApiService.getMaterialAvailability(orderId).catch(() => ({ data: null })),
      ]);

      const productData = (prodRes.data as Record<string, unknown> | null) ?? null;
      setOrder(ord);
      setProduct(productData);
      setMaterialAvailability((avRes.data as LinkedStocksPayload | null) ?? null);

      const status = String(ord.status ?? "");
      const opItems = parseOpProductionItemsFromOrder(ord);
      const { scriptMaterials: smsSeed, scriptResources: srsSeed } =
        extractScriptArraysFromProduct(productData);

      if (status === "iniciado") {
        setMaterialPlanLines({});
        setInsumoPlanLines({});
      } else {
        const { mat, ins } = mergePlanWithDefaults({}, {}, opItems, smsSeed, srsSeed);
        setMaterialPlanLines(mat);
        setInsumoPlanLines(ins);
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || "Falha ao carregar dados da OP");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const ids = new Set<number>();
    for (const lines of Object.values(materialPlanLines)) {
      for (const l of lines) {
        if (l.stockOrResourceId != null) ids.add(l.stockOrResourceId);
      }
    }
    for (const id of ids) {
      void fetchStockRowViaProductionFlowRawMaterials(id).then((d) => {
        if (!d) return;
        setStockCache((p) => {
          if (p[id]) return p;
          return { ...p, [id]: d };
        });
      });
    }
  }, [materialPlanLines]);

  useEffect(() => {
    const ids = new Set<number>();
    for (const lines of Object.values(insumoPlanLines)) {
      for (const l of lines) {
        if (l.stockOrResourceId != null) ids.add(l.stockOrResourceId);
      }
    }
    for (const id of ids) {
      void ApiService.resources.findById(id).then((res) => {
        const d = (res as { data?: unknown }).data;
        if (d && typeof d === "object") {
          setResourceCache((p) => {
            if (p[id]) return p;
            return { ...p, [id]: d as Record<string, unknown> };
          });
        }
      });
    }
  }, [insumoPlanLines]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target;
      if (t instanceof Element && !t.closest(".dropdown-container")) {
        setOpenPicker(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const linkedStocksByScriptMaterialId = useMemo(() => {
    const m = new Map<number, { id: number; label: string }[]>();
    const arr = materialAvailability?.linkedStocksByScriptMaterial;
    if (!arr) return m;
    for (const entry of arr) {
      m.set(
        entry.scriptMaterialId,
        entry.stocks.map((s) => ({
          id: s.id,
          label: (s.description && s.description.trim()) || `Estoque #${s.id}`,
        })),
      );
    }
    return m;
  }, [materialAvailability]);

  const materialAvailabilityByScriptMaterialId = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of materialAvailability?.scriptMaterialAvailability ?? []) {
      map.set(item.scriptMaterialId, item.available);
    }
    return map;
  }, [materialAvailability]);

  const resourceAvailabilityByScriptResourceId = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of materialAvailability?.scriptResourceAvailability ?? []) {
      map.set(item.scriptResourceId, item.available);
    }
    return map;
  }, [materialAvailability]);

  const opProductionItems = useMemo((): FashionOpProductionItem[] => {
    const raw = order?.fashionProductionItems as unknown[] | undefined;
    if (!raw?.length) return [];
    return raw
      .map((it) => {
        const row = it as Record<string, unknown>;
        const id = row.id as number | undefined;
        const fashionOrderId = row.fashionOrderId as number | undefined;
        const quantityForThisProduction = row.quantityForThisProduction as number | undefined;
        if (id == null || fashionOrderId == null) return null;
        return {
          id,
          fashionOrderId,
          quantityForThisProduction: quantityForThisProduction ?? 0,
        };
      })
      .filter((x): x is FashionOpProductionItem => x != null);
  }, [order]);

  const opProductionItemsForDisplay = useMemo(() => {
    if (opProductionItems.length <= 1) return opProductionItems;
    return [opProductionItems[0]];
  }, [opProductionItems]);

  const totalQty = useMemo(() => {
    if (!opProductionItems.length) return 0;
    return opProductionItems.reduce((s, it) => s + (it.quantityForThisProduction || 0), 0);
  }, [opProductionItems]);

  const orderStatus = order?.status as string | undefined;
  const isProductionLocked = orderStatus === "iniciado";
  const fpIdForStart =
    (order?.fashionProductId as number | undefined) ??
    ((order?.fashionProduct as { id?: number } | undefined)?.id);
  const isOpConstructed = Boolean(fpIdForStart && opProductionItems.length > 0);
  const canStartProduction =
    orderStatus === "pending" && isOpConstructed && totalQty > 0;

  const opColorId =
    (order?.colorId as number | undefined) ??
    ((order?.color as { id?: number } | undefined)?.id) ??
    null;

  const productItemColorsFiltered = useMemo(() => {
    const pics = (product?.ProductItemColor as unknown[]) || [];
    if (!opColorId) return pics;
    return pics.filter(
      (p) => (p as { collectionColorId?: number }).collectionColorId === opColorId,
    );
  }, [product, opColorId]);

  const script = useMemo(() => {
    return (product?.Script as unknown[])?.[0] as
      | {
          ScriptMaterial?: unknown[];
          ScriptResource?: unknown[];
        }
      | undefined;
  }, [product]);

  const scriptMaterials = useMemo(() => {
    return (script?.ScriptMaterial || []) as Array<{
      id: number;
      materialId: number;
      weight?: number;
      UnitOfMeasurement?: { name?: string };
      Material?: { name?: string };
    }>;
  }, [script]);

  const scriptResources = useMemo(() => {
    return (script?.ScriptResource || []) as Array<{
      id: number;
      resourceId: number;
      weight?: number;
      UnitOfMeasurement?: { name?: string };
      Resource?: {
        id?: number;
        name?: string;
        stockUnit?: string;
        lengthInMm?: number | null;
        unitQuantity?: number | null;
        materialId?: number | null;
      };
    }>;
  }, [script]);

  useEffect(() => {
    if (isProductionLocked) return;
    setMaterialPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
    for (const sm of scriptMaterials) {
          const k = matCompositeKey(fpi.id, sm.id);
          if (!next[k]?.length) {
            next[k] = [{ key: newKey(`m${fpi.id}-${sm.id}`), stockOrResourceId: null, qty: "" }];
          }
        }
      }
      return next;
    });
  }, [opProductionItems, scriptMaterials, isProductionLocked]);

  useEffect(() => {
    if (isProductionLocked) return;
    setInsumoPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        for (const sr of scriptResources) {
          const k = insCompositeKey(fpi.id, sr.id);
          if (!next[k]?.length) {
            next[k] = [{ key: newKey(`r${fpi.id}-${sr.id}`), stockOrResourceId: null, qty: "" }];
          }
        }
      }
      return next;
    });
  }, [opProductionItems, scriptResources, isProductionLocked]);

  useEffect(() => {
    if (loading || !isProductionLocked || !Number.isFinite(orderId)) return;
    const prefix = fashionMovementOrderPrefix(orderId);
    let cancelled = false;

    (async () => {
      try {
        const list = await fetchActiveFashionMovementsForOrder(orderId);
        const rows = list
          .filter((m) => {
            const on = String(m.orderName ?? "");
            const oid = numOrNull(m.fashionProductionOrderId);
            return (
              (oid === orderId || on.startsWith(prefix)) &&
              isFashionStockMovementActive(m.status)
            );
          })
          .sort(
            (a, b) =>
              Number((a as { id?: number }).id ?? 0) - Number((b as { id?: number }).id ?? 0),
          );
        if (cancelled || orderStatusRef.current !== "iniciado") return;
        if (rows.length === 0) {
          const { mat, ins } = mergePlanWithDefaults(
            {},
            {},
            opProductionItems,
            scriptMaterials,
            scriptResources,
          );
          if (!cancelled && orderStatusRef.current === "iniciado") {
            setMaterialPlanLines(mat);
            setInsumoPlanLines(ins);
          }
          return;
        }

        const matMovs = rows.filter(
          (m) => !FASHION_INS_MOVEMENT_RE.test(String(m.rawMaterialName ?? "")),
        );
        const insMovs = rows.filter((m) =>
          FASHION_INS_MOVEMENT_RE.test(String(m.rawMaterialName ?? "")),
        );

        const matAllExplicit =
          matMovs.length > 0 &&
          matMovs.every(
            (m) =>
              numOrNull(m.fashionConsumedStockId) != null &&
              numOrNull(m.fashionProductionItemId) != null &&
              numOrNull(m.fashionScriptMaterialId) != null,
          );

        const insAllExplicit =
          insMovs.length > 0 &&
          insMovs.every((m) => {
            if (
              numOrNull(m.fashionProductionItemId) == null ||
              numOrNull(m.fashionScriptResourceId) == null
            ) {
              return false;
            }
            if (numOrNull(m.fashionConsumedResourceId) != null) return true;
            return FASHION_INS_MOVEMENT_RE.test(String(m.rawMaterialName ?? ""));
          });

        const matNext: Record<string, PlanLine[]> = {};

        if (matAllExplicit) {
          type MatEntry = { m: (typeof matMovs)[0]; sortId: number };
          const buckets = new Map<string, MatEntry[]>();
          for (const m of matMovs) {
            const fpi = numOrNull(m.fashionProductionItemId)!;
            const fsm = numOrNull(m.fashionScriptMaterialId)!;
            const k = matCompositeKey(fpi, fsm);
            const arr = buckets.get(k) ?? [];
            arr.push({ m, sortId: Number((m as { id?: number }).id ?? 0) });
            buckets.set(k, arr);
          }
          for (const [k, arr] of buckets) {
            arr.sort(
              (a, b) =>
                (numOrNull(a.m.fashionLineSeq) ?? 0) - (numOrNull(b.m.fashionLineSeq) ?? 0) ||
                a.sortId - b.sortId,
            );
            matNext[k] = arr.map((x, i) => ({
              key: newKey(`mov-m-${k}-${i}`),
              stockOrResourceId: numOrNull(x.m.fashionConsumedStockId)!,
              qty: String(x.m.quantity ?? "").trim(),
            }));
          }
        } else {
          const stockCache = new Map<string, number | null>();
          const getStockId = async (code: string, materialId: number) => {
            const ck = `${code}|${materialId}`;
            if (!stockCache.has(ck)) {
              stockCache.set(ck, await resolveStockIdForCodeAndMaterial(code, materialId));
            }
            return stockCache.get(ck) ?? null;
          };

          let mi = 0;
          for (const fpi of opProductionItems) {
    for (const sm of scriptMaterials) {
              const k = matCompositeKey(fpi.id, sm.id);
              const lines: PlanLine[] = [];
              while (mi < matMovs.length) {
                const m = matMovs[mi];
                const code = String(m.rawMaterialName ?? "").trim();
                const sid = await getStockId(code, sm.materialId);
                if (sid == null) break;
                lines.push({
                  key: newKey(`mov-m-${k}-${lines.length}`),
                  stockOrResourceId: sid,
                  qty: String(m.quantity ?? "").trim(),
                });
                mi += 1;
              }
              if (lines.length) matNext[k] = lines;
            }
          }
        }

        const insNext: Record<string, PlanLine[]> = {};

        if (insAllExplicit) {
          type InsEntry = { m: (typeof insMovs)[0]; sortId: number };
          const buckets = new Map<string, InsEntry[]>();
          for (const m of insMovs) {
            const fpi = numOrNull(m.fashionProductionItemId)!;
            const fsr = numOrNull(m.fashionScriptResourceId)!;
            const k = insCompositeKey(fpi, fsr);
            const arr = buckets.get(k) ?? [];
            arr.push({ m, sortId: Number((m as { id?: number }).id ?? 0) });
            buckets.set(k, arr);
          }
          for (const [k, arr] of buckets) {
            arr.sort(
              (a, b) =>
                (numOrNull(a.m.fashionLineSeq) ?? 0) - (numOrNull(b.m.fashionLineSeq) ?? 0) ||
                a.sortId - b.sortId,
            );
            const mapped = arr.map((x, i) => {
              const rid =
                numOrNull(x.m.fashionConsumedResourceId) ??
                (() => {
                  const match = FASHION_INS_MOVEMENT_RE.exec(
                    String(x.m.rawMaterialName ?? ""),
                  );
                  return match ? parseInt(match[1], 10) : NaN;
                })();
              return {
                key: newKey(`mov-i-${k}-${i}`),
                stockOrResourceId: rid,
                qty: String(x.m.quantity ?? "").trim(),
              };
            });
            insNext[k] = mapped.filter(
              (line) =>
                typeof line.stockOrResourceId === "number" &&
                Number.isFinite(line.stockOrResourceId),
            );
          }
        } else {
          let ii = 0;
          for (const fpi of opProductionItems) {
    for (const sr of scriptResources) {
              const k = insCompositeKey(fpi.id, sr.id);
              const matId = sr.Resource?.materialId ?? null;
              const resourceOptions = collectResourcesForScriptResource(
                productItemColorsFiltered,
                matId ?? undefined,
              );
              const allowed = new Set(resourceOptions.map((r) => r.id));
              const lines: PlanLine[] = [];
              while (ii < insMovs.length) {
                const m = insMovs[ii];
                const match = FASHION_INS_MOVEMENT_RE.exec(String(m.rawMaterialName ?? ""));
                const rid = match ? parseInt(match[1], 10) : NaN;
                if (!Number.isFinite(rid) || !allowed.has(rid)) break;
                lines.push({
                  key: newKey(`mov-i-${k}-${lines.length}`),
                  stockOrResourceId: rid,
                  qty: String(m.quantity ?? "").trim(),
                });
                ii += 1;
              }
              if (lines.length) insNext[k] = lines;
            }
          }
        }

        if (cancelled || orderStatusRef.current !== "iniciado") return;
        const { mat, ins } = mergePlanWithDefaults(
          matNext,
          insNext,
          opProductionItems,
          scriptMaterials,
          scriptResources,
        );
        setMaterialPlanLines(mat);
        setInsumoPlanLines(ins);
      } catch {
        if (!cancelled && orderStatusRef.current === "iniciado") {
          const { mat, ins } = mergePlanWithDefaults(
            {},
            {},
            opProductionItems,
            scriptMaterials,
            scriptResources,
          );
          setMaterialPlanLines(mat);
          setInsumoPlanLines(ins);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    isProductionLocked,
    orderId,
    opProductionItems,
    scriptMaterials,
    scriptResources,
    productItemColorsFiltered,
  ]);

  const materialSectionMeta = useMemo(() => {
    return scriptMaterials.map((sm) => {
      const unitName = (sm.UnitOfMeasurement?.name || "").trim();
      const u = unitName.toUpperCase();
      const materialName = sm.Material?.name?.trim() || `Tecido #${sm.materialId}`;
      const colorPart = materialItemColorNameFromPics(productItemColorsFiltered, sm.materialId);
      const colorLabel = colorPart && colorPart !== "—" ? ` - ${colorPart}` : "";
      const baseNeeded = (sm.weight ?? 0) * totalQty;
      const neededConverted = u === "M" ? baseNeeded * 1000 : baseNeeded;
      const available = materialAvailabilityByScriptMaterialId.get(sm.id) ?? 0;
      const formattedAvailable = formatInternalQtyForDisplay(available, unitName || "");
      const usageText = u === "M" ? `${formatQtyLabel(baseNeeded)} M` : `${formatQtyLabel(neededConverted)} ${unitName || "—"}`;
      const title = `Tecido - ${materialName}${colorLabel} — ${usageText}`;
      const availabilityLabel = `(${formattedAvailable} ${unitName || "—"} total em estoque)`;
      const stockOptions = linkedStocksByScriptMaterialId.get(sm.id) ?? [];
      return {
        sm,
        title,
        availabilityLabel,
        stockOptions,
        unitNameForQty: unitName,
      };
    });
  }, [scriptMaterials, productItemColorsFiltered, linkedStocksByScriptMaterialId, totalQty, materialAvailabilityByScriptMaterialId]);

  const insumoSectionMeta = useMemo(() => {
    return scriptResources.map((sr) => {
      const scriptResourceMaterialId = sr.Resource?.materialId;
      const pic = productItemColorsFiltered.find((p) => {
        const res = (p as { Resource?: { materialId?: number | null } }).Resource;
        return res?.materialId === scriptResourceMaterialId;
      });
      const res = (pic as { Resource?: typeof sr.Resource } | undefined)?.Resource || sr.Resource;
      const name = res?.name?.trim() || `Insumo #${sr.resourceId}`;
      const colorPart = insumoItemColorNameFromPic(pic);
      const unitLabel = ((sr.UnitOfMeasurement?.name || res?.stockUnit) || "—").trim();
      const su = unitLabel.toUpperCase();
      const needed = (sr.weight ?? 0) * totalQty;
      const neededConverted = su === "M" ? needed * 1000 : needed;
      const available = resourceAvailabilityByScriptResourceId.get(sr.id) ?? 0;
      const formattedAvailable = formatInternalQtyForDisplay(available, unitLabel || "");
      const usageText = su === "M" ? `${formatQtyLabel(needed)} M` : `${formatQtyLabel(neededConverted)} ${unitLabel || "—"}`;
      const title = `Aviamento - ${name}${colorPart ? ` - ${colorPart}` : ""} — ${usageText}`;
      const availabilityLabel = `(${formattedAvailable} ${unitLabel || "—"} total em estoque)`;
      const resourceOptions = collectResourcesForScriptResource(
        productItemColorsFiltered,
        scriptResourceMaterialId ?? undefined,
      );
      return { sr, title, availabilityLabel, resourceOptions, stockUnitForQty: unitLabel };
    });
  }, [scriptResources, productItemColorsFiltered, totalQty, resourceAvailabilityByScriptResourceId]);

  const addMaterialLine = (_opItemId: number, smId: number) => {
    setMaterialPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = matCompositeKey(fpi.id, smId);
        const existing = next[k] ?? [];
        next[k] = [...existing, { key: newKey(`m${fpi.id}-${smId}`), stockOrResourceId: null, qty: "" }];
      }
      return next;
    });
  };

  const removeMaterialLine = (_opItemId: number, smId: number, lineIndex: number) => {
    setMaterialPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = matCompositeKey(fpi.id, smId);
        const lines = prev[k] || [];
        if (lines.length <= 1) continue;
        const updated = lines.filter((_, index) => index !== lineIndex);
        next[k] = updated.length > 0 ? updated : lines;
      }
      return next;
    });
  };

  const updateMaterialLine = (
    _opItemId: number,
    smId: number,
    lineIndex: number,
    patch: Partial<Pick<PlanLine, "stockOrResourceId" | "qty">>,
  ) => {
    setMaterialPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = matCompositeKey(fpi.id, smId);
        const lines = (prev[k] || []).map((line, idx) =>
          idx === lineIndex ? { ...line, ...patch } : line,
        );
        next[k] = lines;
      }
      return next;
    });
  };

  const addInsumoLine = (_opItemId: number, srId: number) => {
    setInsumoPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = insCompositeKey(fpi.id, srId);
        const existing = next[k] ?? [];
        next[k] = [...existing, { key: newKey(`r${fpi.id}-${srId}`), stockOrResourceId: null, qty: "" }];
      }
      return next;
    });
  };

  const removeInsumoLine = (_opItemId: number, srId: number, lineIndex: number) => {
    setInsumoPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = insCompositeKey(fpi.id, srId);
        const lines = prev[k] || [];
        if (lines.length <= 1) continue;
        const updated = lines.filter((_, index) => index !== lineIndex);
        next[k] = updated.length > 0 ? updated : lines;
      }
      return next;
    });
  };

  const updateInsumoLine = (
    _opItemId: number,
    srId: number,
    lineIndex: number,
    patch: Partial<Pick<PlanLine, "stockOrResourceId" | "qty">>,
  ) => {
    setInsumoPlanLines((prev) => {
      const next = { ...prev };
      for (const fpi of opProductionItems) {
        const k = insCompositeKey(fpi.id, srId);
        const lines = (prev[k] || []).map((line, idx) =>
          idx === lineIndex ? { ...line, ...patch } : line,
        );
        next[k] = lines;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!Number.isFinite(orderId) || !canStartProduction) return;
    setSaving(true);
    try {
      type MatLine = {
        fashionProductionItemId: number;
        scriptMaterialId: number;
        stockId: number;
        quantity: number;
      };
      type InsLine = {
        fashionProductionItemId: number;
        scriptResourceId: number;
        resourceId: number;
        quantity: number;
      };

      const completeMaterialSection = (fpiId: number, smId: number) => {
        const raw = materialPlanLines[matCompositeKey(fpiId, smId)] ?? [];
        return raw.some((line) => {
          const q = parseQtyInput(line.qty);
          return line.stockOrResourceId != null && q != null && q > 0;
        });
      };
      const completeInsumoSection = (fpiId: number, srId: number) => {
        const raw = insumoPlanLines[insCompositeKey(fpiId, srId)] ?? [];
        return raw.some((line) => {
          const q = parseQtyInput(line.qty);
          return line.stockOrResourceId != null && q != null && q > 0;
        });
      };

      if (scriptMaterials.length > 0) {
        for (const fpi of opProductionItems) {
          for (const sm of scriptMaterials) {
            if (!completeMaterialSection(fpi.id, sm.id)) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message:
                  "Preencha todos os campos: Em cada bloco de material, selecione o estoque e informe a quantidade.",
              });
              setSaving(false);
              return;
            }
          }
        }
      }
      if (scriptResources.length > 0) {
        for (const fpi of opProductionItems) {
          for (const sr of scriptResources) {
            if (!completeInsumoSection(fpi.id, sr.id)) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message:
                  "Preencha todos os campos: em cada bloco de insumo, selecione o recurso e informe a quantidade (cada item da OP e cada insumo do script).",
              });
              setSaving(false);
              return;
            }
          }
        }
      }

      const materialLines: MatLine[] = [];
      const insumoLines: InsLine[] = [];

      for (const fpi of opProductionItems) {
        for (const sm of scriptMaterials) {
          const ck = matCompositeKey(fpi.id, sm.id);
          const raw = materialPlanLines[ck] ?? [];
          for (const line of raw) {
            const hasStock = line.stockOrResourceId != null;
            const q = parseQtyInput(line.qty);
            const hasQty = q != null && q > 0;
            if (!hasStock && !hasQty) continue;
            if (!hasStock || !hasQty) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message: "Preencha estoque e quantidade em cada linha de material (sem deixar só um dos dois).",
              });
              setSaving(false);
              return;
            }
            materialLines.push({
              fashionProductionItemId: fpi.id,
              scriptMaterialId: sm.id,
              stockId: line.stockOrResourceId!,
              quantity: q!,
            });
          }
        }
      }

      for (const fpi of opProductionItems) {
        for (const sr of scriptResources) {
          const ck = insCompositeKey(fpi.id, sr.id);
          const raw = insumoPlanLines[ck] ?? [];
          for (const line of raw) {
            const hasRes = line.stockOrResourceId != null;
            const q = parseQtyInput(line.qty);
            const hasQty = q != null && q > 0;
            if (!hasRes && !hasQty) continue;
            if (!hasRes || !hasQty) {
              setFeedback({
                isOpen: true,
                statusOk: false,
                message: "Preencha insumo e quantidade em cada linha (sem deixar só um dos dois).",
              });
              setSaving(false);
              return;
            }
            insumoLines.push({
              fashionProductionItemId: fpi.id,
              scriptResourceId: sr.id,
              resourceId: line.stockOrResourceId!,
              quantity: q!,
            });
          }
        }
      }

      await FashionApiService.startFashionProduction(orderId, {
        materialLines,
        insumoLines,
      });
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: "Produção iniciada e estoque abatido com sucesso.",
      });
      setRefresh((r) => !r);
    navigate("/panel/production-fashion");
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string | string[] } } }).response
              ?.data?.message
          : null;
      const text = Array.isArray(msg) ? msg.join("; ") : msg || "Falha ao iniciar produção.";
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: text,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!Number.isFinite(orderId)) return;
    if (
      !window.confirm(
        "Reverter? As movimentações de estoque desta OP serão estornadas e o status voltará para pendente.",
      )
    ) {
      return;
    }
    setReverting(true);
    try {
      await FashionApiService.revertFashionProductionStart(orderId);
      await load();
      setRefresh((r) => !r);
      eventBus.emit("refreshProductionTable");
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: "Produção revertida com sucesso.",
      });
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: msg || "Falha ao reverter a produção.",
      });
    } finally {
      setReverting(false);
    }
  };

  if (loading) {
    return (
      <MainContainer style={pageMainStyle}>
        <p style={{ padding: "2rem" }}>Carregando…</p>
      </MainContainer>
    );
  }

  if (error) {
    return (
      <MainContainer style={pageMainStyle}>
        <p style={{ padding: "2rem", color: "coral" }}>{error}</p>
        <CancelButton type="button" onClick={() => navigate("/panel/production-fashion")}>
          Voltar
        </CancelButton>
      </MainContainer>
    );
  }

  return (
    <MainContainer style={pageMainStyle}>
      <StyledCard style={{ marginBottom: 12 }}>
        <SectionName style={{ margin: 0 }}>Iniciar produção — OP-{orderId}</SectionName>
      </StyledCard>
      <BreadcrumbsContainer style={{ marginBottom: 8, padding: "0.25rem 0 4px" }}>
        <ol>
          <li>
            <Link to="/panel/production-fashion">Produção</Link>
          </li>
          <li>
            <span>Materiais e insumos</span>
          </li>
        </ol>
      </BreadcrumbsContainer>

      {orderStatus === "iniciado" && (
        <p
          style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: 8,
            background: "#fff8e6",
            border: "1px solid #f0d78c",
            color: "#5c4a00",
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          Produção iniciada: os valores abaixo são somente leitura. Use &quot;Reverter&quot; para estornar o
          abatimento e voltar a editar.
        </p>
      )}
      {orderStatus === "pending" && !isOpConstructed && (
        <p
          style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: 8,
            background: "#fff8e6",
            border: "1px solid #f0d78c",
            color: "#5c4a00",
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          A OP ainda não está montada (produto ou itens faltando). Conclua a montagem no PCP antes de
          iniciar a produção.
        </p>
      )}
      {orderStatus === "pending" && isOpConstructed && totalQty === 0 && (
        <p
          style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: 8,
            background: "#fff8e6",
            border: "1px solid #f0d78c",
            color: "#5c4a00",
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          A soma das quantidades dos pedidos nesta OP é zero. Ajuste as quantidades na montagem da OP
          no PCP antes de iniciar.
        </p>
      )}

      {opProductionItemsForDisplay.flatMap((fpi) => [
        ...materialSectionMeta.map((sec) => ({ kind: "material" as const, fpi, sec })),
        ...insumoSectionMeta.map((sec) => ({ kind: "insumo" as const, fpi, sec })),
      ]).map((cell) => {
        if (cell.kind === "material") {
          const { fpi, sec } = cell;
          const ck = matCompositeKey(fpi.id, sec.sm.id);
          const lines = materialPlanLines[ck] || [
            { key: newKey("m"), stockOrResourceId: null, qty: "" },
          ];
          return (
            <StyledCard key={`m-${fpi.id}-${sec.sm.id}`}>
              <CardTitle style={{ marginTop: 0, paddingBottom: 16 }}>
                {sec.title}{' '}
                <span style={{ color: '#0090d3' }}>{sec.availabilityLabel}</span>
              </CardTitle>
              {lines.map((line) => {
                const matOpts = optionsExcludingUsedOnOtherLines(
                  sec.stockOptions,
                  lines,
                  line.key,
                  line.stockOrResourceId,
                );
                const matPickerId = `mat-${fpi.id}-${sec.sm.id}-${line.key}`;
                const matOpen = openPicker === matPickerId;
                const selMat = matOpts.find((o) => o.id === line.stockOrResourceId);
                const matStock = line.stockOrResourceId != null ? stockCache[line.stockOrResourceId] : null;
                const matDisplayUnit = unitLabelForDisplay(sec.unitNameForQty || "");
                const matAvail = matStock != null
                  ? stockAvailableForMaterialRow(matStock, sec.unitNameForQty || "")
                  : null;
                const matTyped = parseQtyInput(line.qty) ?? 0;
                console.debug('Material row', {fpiId: fpi.id, scriptMaterialId: sec.sm.id, lineKey: line.key, selectedStockId: line.stockOrResourceId, matStock, matAvail, title: sec.title});
                const matAfter =
                  matAvail != null
                    ? matAvail - qtyInputToInternal(matTyped, sec.unitNameForQty || "")
                    : null;
                return (
                <div key={line.key} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 20,
                      }}
                    >
          <StyledFormGroup className="dropdown-container" style={{ position: "relative" }}>
                        <label>Material</label>
                        <input
                          type="text"
                          autoComplete="off"
                          disabled={isProductionLocked}
                          value={matOpen ? pickerQuery : selMat?.label ?? ""}
                          placeholder="Buscar estoque…"
                          onChange={(e) => {
                            setPickerQuery(e.target.value);
                            setOpenPicker(matPickerId);
                          }}
                          onFocus={() => {
                            setOpenPicker(matPickerId);
                            setPickerQuery(matOpen ? pickerQuery : selMat?.label ?? "");
                          }}
                        />
                        {matOpen && !isProductionLocked && (
                          <DropdownList>
                            {matOpts
                              .filter((o) =>
                                o.label.toLowerCase().includes(pickerQuery.trim().toLowerCase()),
                              )
                              .map((o) => (
                                <DropdownItem
                                  key={o.id}
                                  onClick={() => {
                                    const lineIndex = lines.findIndex((l) => l.key === line.key);
                                    updateMaterialLine(fpi.id, sec.sm.id, lineIndex, {
                                      stockOrResourceId: o.id,
                                    });
                                    setOpenPicker(null);
                                    setPickerQuery("");
                                  }}
                                >
                                  {o.label}
                                </DropdownItem>
                              ))}
                            {matOpts.filter((o) =>
                              o.label.toLowerCase().includes(pickerQuery.trim().toLowerCase()),
                            ).length === 0 && (
                              <DropdownItem style={{ pointerEvents: "none", opacity: 0.65 }}>
                                Nenhum resultado
                              </DropdownItem>
                            )}
                          </DropdownList>
                        )}
          </StyledFormGroup>
          <StyledFormGroup>
                        <label>
                          Quantidade —{" "}
                          <span style={{ color: "#0090d3", fontWeight: 700 }}>
                            {matAvail != null
                              ? `${formatInternalQtyForDisplay(matAvail, sec.unitNameForQty || "")} ${matDisplayUnit}`
                              : "—"}
                            {matTyped > 0 && matAfter != null
                              ? ` — ${formatInternalQtyForDisplay(matAfter, sec.unitNameForQty || "")} ${matDisplayUnit}`
                              : ""}
                          </span>
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder=""
                          autoComplete="off"
                          readOnly={isProductionLocked}
                          disabled={isProductionLocked}
                          value={line.qty}
                          onChange={(e) =>
                            updateMaterialLine(fpi.id, sec.sm.id, lines.findIndex((l) => l.key === line.key), {
                              qty: e.target.value,
                            })
                          }
                        />
                      </StyledFormGroup>
                    </div>
                    {!isProductionLocked && lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMaterialLine(fpi.id, sec.sm.id, lines.findIndex((l) => l.key === line.key))}
                        title="Remover linha"
                        aria-label="Remover linha"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 6,
                          color: "#00bcd4",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <img src={trash} alt="" style={{ width: 20, height: 20, objectFit: "contain" }} />
                      </button>
                    )}
                  </div>
                </div>
              );
              })}
              {!isProductionLocked && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                  <AddButton type="button" onClick={() => addMaterialLine(fpi.id, sec.sm.id)}>
                    <img src={plus} alt="" />
                    Adicionar material
                  </AddButton>
                </div>
              )}
      </StyledCard>
          );
        }
        const { fpi, sec } = cell;
        const ck = insCompositeKey(fpi.id, sec.sr.id);
        const lines = insumoPlanLines[ck] || [
          { key: newKey("r"), stockOrResourceId: null, qty: "" },
        ];
        return (
          <StyledCard key={`r-${fpi.id}-${sec.sr.id}`}>
            <CardTitle style={{ marginTop: 0, paddingBottom: 16 }}>
              {sec.title}{' '}
              <span style={{ color: '#0090d3' }}>{sec.availabilityLabel}</span>
            </CardTitle>
            {lines.map((line) => {
              const insOpts = optionsExcludingUsedOnOtherLines(
                sec.resourceOptions,
                lines,
                line.key,
                line.stockOrResourceId,
              );
              const insPickerId = `ins-${fpi.id}-${sec.sr.id}-${line.key}`;
              const insOpen = openPicker === insPickerId;
              const selIns = insOpts.find((o) => o.id === line.stockOrResourceId);
              const insRes =
                line.stockOrResourceId != null ? resourceCache[line.stockOrResourceId] : null;
              const insDisplayUnit = unitLabelForDisplay(sec.stockUnitForQty || "");
              const insAvail = insRes != null
                ? resourceAvailableAmount(insRes)
                : null;
              const insTyped = parseQtyInput(line.qty) ?? 0;
              console.debug('Insumo row', {fpiId: fpi.id, scriptResourceId: sec.sr.id, lineKey: line.key, selectedResourceId: line.stockOrResourceId, insRes, insAvail, title: sec.title});
              const insAfter =
                insAvail != null
                  ? insAvail - qtyInputToInternal(insTyped, sec.stockUnitForQty || "")
                  : null;
              return (
              <div key={line.key} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                    }}
                  >
          <StyledFormGroup className="dropdown-container" style={{ position: "relative" }}>
                      <label>Insumo</label>
                      <input
                        type="text"
                        autoComplete="off"
                        disabled={isProductionLocked}
                        value={insOpen ? pickerQuery : selIns?.label ?? ""}
                        placeholder="Buscar insumo…"
                        onChange={(e) => {
                          setPickerQuery(e.target.value);
                          setOpenPicker(insPickerId);
                        }}
                        onFocus={() => {
                          setOpenPicker(insPickerId);
                          setPickerQuery(insOpen ? pickerQuery : selIns?.label ?? "");
                        }}
                      />
                      {insOpen && !isProductionLocked && (
                        <DropdownList>
                          {insOpts
                            .filter((o) =>
                              o.label.toLowerCase().includes(pickerQuery.trim().toLowerCase()),
                            )
                            .map((o) => (
                              <DropdownItem
                                key={o.id}
                                onClick={() => {
                                  const lineIndex = lines.findIndex((l) => l.key === line.key);
                                  updateInsumoLine(fpi.id, sec.sr.id, lineIndex, {
                                    stockOrResourceId: o.id,
                                  });
                                  setOpenPicker(null);
                                  setPickerQuery("");
                                }}
                              >
                                {o.label}
                              </DropdownItem>
                            ))}
                          {insOpts.filter((o) =>
                            o.label.toLowerCase().includes(pickerQuery.trim().toLowerCase()),
                          ).length === 0 && (
                            <DropdownItem style={{ pointerEvents: "none", opacity: 0.65 }}>
                              Nenhum resultado
                            </DropdownItem>
                          )}
                        </DropdownList>
                      )}
          </StyledFormGroup>
                    <StyledFormGroup>
                      <label>
                        Quantidade —{" "}
                        <span style={{ color: "#0090d3", fontWeight: 700 }}>
                          {insAvail != null
                            ? `${formatInternalQtyForDisplay(insAvail, sec.stockUnitForQty || "")} ${insDisplayUnit}`
                            : "—"}
                          {insTyped > 0 && insAfter != null
                            ? ` — ${formatInternalQtyForDisplay(insAfter, sec.stockUnitForQty || "")} ${insDisplayUnit}`
                            : ""}
                        </span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder=""
                        autoComplete="off"
                        readOnly={isProductionLocked}
                        disabled={isProductionLocked}
                        value={line.qty}
                        onChange={(e) => {
                          const lineIndex = lines.findIndex((l) => l.key === line.key);
                          updateInsumoLine(fpi.id, sec.sr.id, lineIndex, {
                            qty: e.target.value,
                          });
                        }}
                      />
                    </StyledFormGroup>
                  </div>
                  {!isProductionLocked && lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInsumoLine(fpi.id, sec.sr.id, lines.findIndex((l) => l.key === line.key))}
                      title="Remover linha"
                      aria-label="Remover linha"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 6,
                        color: "#00bcd4",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <img src={trash} alt="" style={{ width: 20, height: 20, objectFit: "contain" }} />
                    </button>
                  )}
                </div>
              </div>
            );
            })}
            {!isProductionLocked && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                <AddButton type="button" onClick={() => addInsumoLine(fpi.id, sec.sr.id)}>
                  <img src={plus} alt="" />
                  Adicionar insumo
                </AddButton>
              </div>
            )}
      </StyledCard>
        );
      })}

      <StyledCard style={{ marginBottom: 0 }}>
        <ActionButtons style={{ width: "100%", flexWrap: "wrap", gap: 12 }}>
          {isProductionLocked && (
            <CancelButton
              type="button"
              disabled={reverting}
              onClick={handleRevert}
              style={{ marginRight: "auto", borderColor: "#c53030", color: "#c53030" }}
            >
              {reverting ? "Revertendo…" : "Reverter"}
            </CancelButton>
          )}
        <CancelButton type="button" onClick={() => navigate("/panel/production-fashion")}>
          Cancelar
        </CancelButton>
          <SaveButton
            type="button"
            onClick={handleSave}
            disabled={saving || !canStartProduction || isProductionLocked}
          >
          <img src={save} alt="" style={{ marginRight: 8 }} />
            {saving ? "Salvando…" : "Salvar"}
        </SaveButton>
      </ActionButtons>
      </StyledCard>
    </MainContainer>
  );
};

export default StartFashionProduction;
